// import { forwardRef, useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
// import styles from './OTPInput.module.scss';

// export interface OTPInputProps {
//   /** Number of OTP digits */
//   length?: number;
//   /** Input name */
//   name: string;
//   /** Label */
//   label?: string;
//   /** Error message */
//   error?: string;
//   /** Callback when OTP is complete */
//   onComplete?: (otp: string) => void;
//   /** Callback when OTP changes */
//   onChange?: (otp: string) => void;
//   /** Disabled state */
//   disabled?: boolean;
//   /** Required field */
//   required?: boolean;
//   /** Auto-focus first input */
//   autoFocus?: boolean;
// }

// /**
//  * OTPInput Component
//  *
//  * 6-digit OTP input with auto-advance and paste support
//  *
//  * @example
//  * ```tsx
//  * <OTPInput
//  *   name="otp"
//  *   label="Enter verification code"
//  *   length={6}
//  *   onComplete={(otp) => verifyOTP(otp)}
//  *   error={errors.otp?.message}
//  * />
//  * ```
//  */
// export const OTPInput = forwardRef<HTMLInputElement, OTPInputProps>(
//   (
//     {
//       length = 6,
//       name,
//       label,
//       error,
//       onComplete,
//       onChange,
//       disabled = false,
//       required = false,
//       autoFocus = true,
//     },
//     ref
//   ) => {
//     const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
//     const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
//     const hasError = Boolean(error);

//     // Initialize refs array
//     useEffect(() => {
//       inputRefs.current = inputRefs.current.slice(0, length);
//     }, [length]);

//     // Auto-focus first input
//     useEffect(() => {
//       if (autoFocus && inputRefs.current[0]) {
//         inputRefs.current[0].focus();
//       }
//     }, [autoFocus]);

//     const handleChange = (index: number, value: string) => {
//       // Only allow digits
//       const digit = value.replace(/[^0-9]/g, '');

//       if (digit.length > 1) {
//         // Handle paste of multiple digits
//         handlePasteDigits(digit, index);
//         return;
//       }

//       const newOtp = [...otp];
//       newOtp[index] = digit;
//       setOtp(newOtp);

//       // Call onChange
//       const otpString = newOtp.join('');
//       if (onChange) {
//         onChange(otpString);
//       }

//       // Move to next input if digit entered
//       if (digit && index < length - 1) {
//         inputRefs.current[index + 1]?.focus();
//       }

//       // Call onComplete if all digits filled
//       if (digit && newOtp.every((d) => d !== '')) {
//         if (onComplete) {
//           onComplete(newOtp.join(''));
//         }
//       }
//     };

//     const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
//       // Backspace: clear current and move to previous
//       if (e.key === 'Backspace') {
//         e.preventDefault();
//         const newOtp = [...otp];

//         if (otp[index]) {
//           // Clear current
//           newOtp[index] = '';
//           setOtp(newOtp);
//         } else if (index > 0) {
//           // Move to previous and clear
//           newOtp[index - 1] = '';
//           setOtp(newOtp);
//           inputRefs.current[index - 1]?.focus();
//         }

//         if (onChange) {
//           onChange(newOtp.join(''));
//         }
//       }

//       // Arrow Left: move to previous
//       if (e.key === 'ArrowLeft' && index > 0) {
//         e.preventDefault();
//         inputRefs.current[index - 1]?.focus();
//       }

//       // Arrow Right: move to next
//       if (e.key === 'ArrowRight' && index < length - 1) {
//         e.preventDefault();
//         inputRefs.current[index + 1]?.focus();
//       }
//     };

//     const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
//       e.preventDefault();
//       const pasteData = e.clipboardData.getData('text/plain');
//       const digits = pasteData.replace(/[^0-9]/g, '').slice(0, length);

//       if (digits) {
//         handlePasteDigits(digits, 0);
//       }
//     };

//     const handlePasteDigits = (digits: string, startIndex: number) => {
//       const newOtp = [...otp];
//       const digitsArray = digits.split('');

//       digitsArray.forEach((digit, i) => {
//         const index = startIndex + i;
//         if (index < length) {
//           newOtp[index] = digit;
//         }
//       });

//       setOtp(newOtp);

//       // Focus last filled input or next empty
//       const lastFilledIndex = Math.min(startIndex + digitsArray.length, length) - 1;
//       inputRefs.current[lastFilledIndex]?.focus();

//       // Call callbacks
//       const otpString = newOtp.join('');
//       if (onChange) {
//         onChange(otpString);
//       }

//       if (newOtp.every((d) => d !== '') && onComplete) {
//         onComplete(otpString);
//       }
//     };

//     const handleFocus = (index: number) => {
//       // Select content on focus for easy replacement
//       inputRefs.current[index]?.select();
//     };

//     return (
//       <div className={styles.container}>
//         {label && (
//           <label className={styles.label}>
//             {label}
//             {required && <span className={styles.required} aria-label="required">*</span>}
//           </label>
//         )}

//         <div
//           className={styles.inputsWrapper}
//           role="group"
//           aria-labelledby={label ? `${name}-label` : undefined}
//         >
//           {otp.map((digit, index) => (
//             <input
//               key={index}
//               ref={(el) => {
//                 inputRefs.current[index] = el;
//                 // Forward ref to first input
//                 if (index === 0 && ref) {
//                   if (typeof ref === 'function') {
//                     ref(el);
//                   } else {
//                     ref.current = el;
//                   }
//                 }
//               }}
//               type="text"
//               inputMode="numeric"
//               pattern="[0-9]"
//               maxLength={1}
//               value={digit}
//               onChange={(e) => handleChange(index, e.target.value)}
//               onKeyDown={(e) => handleKeyDown(index, e)}
//               onPaste={handlePaste}
//               onFocus={() => handleFocus(index)}
//               disabled={disabled}
//               aria-label={`Digit ${index + 1}`}
//               aria-invalid={hasError}
//               className={`${styles.input} ${hasError ? styles.inputError : ''} ${digit ? styles.filled : ''}`}
//             />
//           ))}
//         </div>

//         {/* Hidden input with full OTP value */}
//         <input type="hidden" name={name} value={otp.join('')} />

//         {error && (
//           <p className={styles.error} role="alert">
//             {error}
//           </p>
//         )}
//       </div>
//     );
//   }
// );

// OTPInput.displayName = 'OTPInput';

/****************************************************************************** */
/*                          Gemini Modified Code                                */
/****************************************************************************** */

import {
  forwardRef,
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  ClipboardEvent,
} from "react";
import styles from "./OTPInput.module.scss";

export interface OTPInputProps {
  /** Number of OTP digits */
  length?: number;
  /** Input name */
  name: string;
  /** Label */
  label?: string;
  /** Error message */
  error?: string;
  /** Callback when OTP is complete */
  onComplete?: (otp: string) => void;
  /** Callback when OTP changes */
  onChange?: (otp: string) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Required field */
  required?: boolean;
  /** Auto-focus first input */
  autoFocus?: boolean;
  /** Controlled value (Required for React Hook Form Controller) */
  value?: string;
}

/**
 * OTPInput Component
 * * 6-digit OTP input with auto-advance and paste support
 * * @example
 * ```tsx
 * <OTPInput
 * name="otp"
 * label="Enter verification code"
 * length={6}
 * onComplete={(otp) => verifyOTP(otp)}
 * error={errors.otp?.message}
 * />
 * ```
 */
export const OTPInput = forwardRef<HTMLInputElement, OTPInputProps>(
  (
    {
      length = 6,
      name,
      label,
      error,
      onComplete,
      onChange,
      disabled = false,
      required = false,
      autoFocus = true,
      value, // Destructure controlled value
    },
    ref
  ) => {
    const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const hasError = Boolean(error);

    // Initialize refs array
    useEffect(() => {
      inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    // -------------------------------------------------------------
    // 🔄 SYNC CONTROLLED VALUE (Added for React Hook Form)
    // -------------------------------------------------------------
    useEffect(() => {
      if (value !== undefined) {
        // Split the controlled string value into the array format needed for rendering
        const newOtp = value.split("").slice(0, length);
        // Fill remaining with empty strings if value is shorter than length
        while (newOtp.length < length) {
          newOtp.push("");
        }
        setOtp(newOtp);
      }
    }, [value, length]);

    // Auto-focus first input
    useEffect(() => {
      if (autoFocus && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, [autoFocus]);

    const handleChange = (index: number, inputValue: string) => {
      // Only allow digits
      const digit = inputValue.replace(/[^0-9]/g, "");

      if (digit.length > 1) {
        // Handle paste of multiple digits
        handlePasteDigits(digit, index);
        return;
      }

      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);

      // Call onChange with the joined string
      const otpString = newOtp.join("");
      if (onChange) {
        onChange(otpString);
      }

      // Move to next input if digit entered
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Call onComplete if all digits filled
      if (digit && newOtp.every((d) => d !== "")) {
        if (onComplete) {
          onComplete(newOtp.join(""));
        }
      }
    };

    const handleKeyDown = (
      index: number,
      e: KeyboardEvent<HTMLInputElement>
    ) => {
      // Backspace: clear current and move to previous
      if (e.key === "Backspace") {
        e.preventDefault();
        const newOtp = [...otp];

        if (otp[index]) {
          // Clear current
          newOtp[index] = "";
          setOtp(newOtp);
        } else if (index > 0) {
          // Move to previous and clear
          newOtp[index - 1] = "";
          setOtp(newOtp);
          inputRefs.current[index - 1]?.focus();
        }

        if (onChange) {
          onChange(newOtp.join(""));
        }
      }

      // Arrow Left: move to previous
      if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }

      // Arrow Right: move to next
      if (e.key === "ArrowRight" && index < length - 1) {
        e.preventDefault();
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasteData = e.clipboardData.getData("text/plain");
      const digits = pasteData.replace(/[^0-9]/g, "").slice(0, length);

      if (digits) {
        handlePasteDigits(digits, 0);
      }
    };

    const handlePasteDigits = (digits: string, startIndex: number) => {
      const newOtp = [...otp];
      const digitsArray = digits.split("");

      digitsArray.forEach((digit, i) => {
        const index = startIndex + i;
        if (index < length) {
          newOtp[index] = digit;
        }
      });

      setOtp(newOtp);

      // Focus last filled input or next empty
      const lastFilledIndex =
        Math.min(startIndex + digitsArray.length, length) - 1;
      inputRefs.current[lastFilledIndex]?.focus();

      // Call callbacks
      const otpString = newOtp.join("");
      if (onChange) {
        onChange(otpString);
      }

      if (newOtp.every((d) => d !== "") && onComplete) {
        onComplete(otpString);
      }
    };

    const handleFocus = (index: number) => {
      // Select content on focus for easy replacement
      inputRefs.current[index]?.select();
    };

    return (
      <div className={styles.container}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div
          className={styles.inputsWrapper}
          role="group"
          aria-labelledby={label ? `${name}-label` : undefined}
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
                // Forward ref to first input
                if (index === 0 && ref) {
                  if (typeof ref === "function") {
                    ref(el);
                  } else {
                    ref.current = el;
                  }
                }
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => handleFocus(index)}
              disabled={disabled}
              aria-label={`Digit ${index + 1}`}
              aria-invalid={hasError}
              className={`${styles.input} ${hasError ? styles.inputError : ""} ${digit ? styles.filled : ""}`}
            />
          ))}
        </div>

        {/* Hidden input with full OTP value for standard form submissions */}
        <input type="hidden" name={name} value={otp.join("")} />

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

OTPInput.displayName = "OTPInput";
